interface User {
  id: string;
  canonicalId?: string;
  email: string;
  name: string;
  avatar?: string;
  bio?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  kudozBalance?: number;
  lifetimeKudozEarned?: number;
  accountBalance?: string;
  trustScore?: number;
  trustTier?: 'newbie' | 'reliable' | 'trusted' | 'elite';
  totalReviews?: number;
  totalOrders?: number;
  profileCompletionPercent?: number;
  followersCount?: number;
  followingCount?: number;
  isGuestAccount?: boolean;
}

interface AuthResponse {
  token: string;
  user: User;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export class AuthService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'auth_user';
  private readonly USER_CACHE_KEY = 'user_profile_cache';
  private readonly EATZY_CACHE_KEY = 'eatzy_profile_cache';
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly EATZY_REFRESH_TOKEN_KEY = 'eatzyRefreshToken';
  private readonly EATZY_TOKEN_EXPIRES_KEY = 'eatzyTokenExpires';
  private _eatzyRefreshing = false; // guard against concurrent refresh calls

  /**
   * Extract error message from API response
   */
  private async extractErrorMessage(response: Response, defaultMessage: string): Promise<string> {
    try {
      const error = await response.json();
      console.log('[AuthService] Error response:', JSON.stringify(error));
      
      // Handle different error response formats from Eatzy auth
      // The API may return { code: "INVALID_EMAIL_OR_PASSWORD" } or { error: "..." } or { message: "..." }
      let errorMessage = error.message || error.error || defaultMessage;
      
      // Translate error codes to user-friendly messages
      if (error.code === 'INVALID_EMAIL_OR_PASSWORD') {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
      } else if (error.code) {
        // Use the code if no better message is available
        errorMessage = error.code.replace(/_/g, ' ').toLowerCase();
      }
      
      return errorMessage;
    } catch (parseError) {
      // If response is not JSON, use status text or generic message
      console.warn('[AuthService] Failed to parse error response:', parseError);
      return response.statusText || `${defaultMessage} (${response.status})`;
    }
  }

  /**
   * Get current auth state from localStorage
   */
  getAuthState(): AuthState {
    let token = localStorage.getItem(this.TOKEN_KEY);
    if (token === 'undefined' || token === 'null' || token === '') {
      localStorage.removeItem(this.TOKEN_KEY);
      token = null;
    }
    const userStr = localStorage.getItem(this.USER_KEY);
    
    let user: User | null = null;
    if (userStr && userStr !== 'undefined' && userStr !== 'null') {
      try {
        user = JSON.parse(userStr);
        // Validate parsed user has required fields
        if (!user || typeof user !== 'object' || !user.id || !user.email) {
          console.warn('⚠️ Stored user is missing required fields, clearing');
          user = null;
          this.clearAuth();
        }
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem(this.USER_KEY);
      }
    } else if (userStr === 'undefined' || userStr === 'null') {
      // Clean up invalid stored values
      console.warn('⚠️ Cleaning up invalid stored user value:', userStr);
      localStorage.removeItem(this.USER_KEY);
    }

    return {
      user,
      token,
      isAuthenticated: !!(token && user),
      isLoading: false
    };
  }

  /**
   * Sign up with email and password
   */
  async signUp(email: string, password: string, firstName: string, lastName: string): Promise<AuthResponse> {
    // Call backend auth route which handles proxying to Eatzy
    const name = `${firstName} ${lastName}`.trim();
    const response = await fetch('/api/auth/sign-up', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        email, 
        password, 
        name,
        firstName,
        lastName,
      })
    });

    if (!response.ok) {
      const errorMessage = await this.extractErrorMessage(response, 'Sign up failed');
      throw new Error(errorMessage);
    }

    const data = await response.json();

    console.log('[AuthService] Sign up response:', JSON.stringify(data));

    // Store eatzyAccessToken if the backend returned one (mirrors sign-in behaviour).
    // This allows Kudoz wallet sync to work immediately after sign-up.
    if (data.eatzyAccessToken) {
      sessionStorage.setItem('adminEatzyToken', data.eatzyAccessToken);
      sessionStorage.setItem('partnerEatzyToken', data.eatzyAccessToken);
      localStorage.setItem('eatzyUserToken', data.eatzyAccessToken);
      if (data.eatzyRefreshToken) localStorage.setItem(this.EATZY_REFRESH_TOKEN_KEY, data.eatzyRefreshToken);
      if (data.eatzyTokenExpires) localStorage.setItem(this.EATZY_TOKEN_EXPIRES_KEY, String(data.eatzyTokenExpires));
    }
    
    // Fetch full user profile from backend to get kudoz balance
    try {
      // Validate token before fetching profile
      if (!data.token || typeof data.token !== 'string') {
        console.warn('Invalid token received during sign up');
        return data;
      }

      const fullUser = await this.fetchUserProfile(data.token);

      const enrichedUser = { ...data.user, ...fullUser };
      this.setAuth(data.token, enrichedUser);
      return { ...data, user: enrichedUser };
    } catch (error) {
      // If profile fetch fails, continue with basic user data
      console.warn('Failed to fetch full user profile:', error);
      this.setAuth(data.token, data.user);
      return data;
    }
  }

  /**
   * Get current session from server (cookie-based)
   */
  async getSession(): Promise<{ user: User; session: any } | null> {
    try {
      const response = await fetch('/api/auth/session', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important: Include cookies
      });

      if (!response.ok) {
        console.warn(`[AuthService] getSession failed: ${response.status} ${response.statusText}`);
        const text = await response.text();
        console.warn(`[AuthService] Response body: ${text}`);
        return null;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get session error:', error);
      return null;
    }
  }

  /**
   * Sign in with Google OAuth
   * Uses server-side redirect flow to ensure secrets stay server-side
   */
  signInWithGoogle(): void {
    console.log('🔧 Initiating Google Sign In via server redirect...');
    window.location.href = '/api/auth/google';
  }

  /**
   * Sign in with Facebook OAuth
   */
  signInWithFacebook(): void {
    window.location.href = '/api/auth/facebook';
  }

  /**
   * Sign in with Apple OAuth
   */
  signInWithApple(): void {
    window.location.href = '/api/auth/apple';
  }

  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string): Promise<AuthResponse> {
    // Clear any existing auth data before attempting login
    // This prevents old session cookies from interfering with new login attempts
    this.clearAllAuthData();
    console.log('[AuthService] Cleared existing auth data before login attempt');
    
    // Call backend auth route which handles password hashing internally
    const response = await fetch('/api/auth/sign-in', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    let data: any = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (
      !response.ok ||
      !data ||
      data.error ||
      data.code ||
      data.success === false
    ) {
      const errorMessage =
        data?.message ||
        data?.error ||
        data?.code ||
        'Sign in failed';
      throw new Error(errorMessage);
    }
    
    if (data.eatzyAccessToken) {
      sessionStorage.setItem('adminEatzyToken', data.eatzyAccessToken);
      sessionStorage.setItem('partnerEatzyToken', data.eatzyAccessToken);
      localStorage.setItem('eatzyUserToken', data.eatzyAccessToken);
      if (data.eatzyRefreshToken) localStorage.setItem(this.EATZY_REFRESH_TOKEN_KEY, data.eatzyRefreshToken);
      if (data.eatzyTokenExpires) localStorage.setItem(this.EATZY_TOKEN_EXPIRES_KEY, String(data.eatzyTokenExpires));
    }

    // Fetch full user profile from backend to get kudoz balance
    try {
      const fullUser = await this.fetchUserProfile(data.token);
      const enrichedUser = { ...data.user, ...fullUser };
      this.setAuth(data.token, enrichedUser);
      return { ...data, user: enrichedUser };
    } catch (error) {
      // If profile fetch fails, continue with basic user data
      console.warn('Failed to fetch full user profile:', error);
      this.setAuth(data.token, data.user);
      return data;
    }
  }

  /**
   * Sign out
   */
  async signOut(): Promise<void> {
    try {
      const token = localStorage.getItem(this.TOKEN_KEY);
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      await fetch('/api/auth/sign-out', {
        method: 'POST',
        headers,
      });
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      this.clearAuth();
    }
  }

  /**
   * Verify current token
   */
  async verifyToken(token?: string): Promise<User | null> {
    try {
      const rawToken = token || localStorage.getItem(this.TOKEN_KEY);
      const localToken = rawToken && rawToken !== 'undefined' && rawToken !== 'null' ? rawToken : null;
      if (localToken) {
        console.log('🔑 Using localStorage token for verification');
        const response = await fetch('/api/auth/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: localToken })
        });

        if (!response.ok) {
          return null;
        }

        const data = await response.json();
        if (!data.valid) {
          return null;
        }

        // Fetch full user profile using token-based auth
        try {
          const fullUser = await this.fetchUserProfile(localToken);
          return { ...data.user, ...fullUser };
        } catch (error) {
          // If profile fetch fails, return basic user data
          console.warn('Failed to fetch full user profile during verification:', error);
          return data.user;
        }
      }
      return null;
    } catch (error) {
      console.error('Token verification error:', error);
      return null;
    }
  }

  /**
   * Get auth headers for API calls
   */
  getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem(this.TOKEN_KEY);
    if (token && token !== 'undefined' && token !== 'null') {
      console.log('🔑 Using localStorage token for Authorization header');
      return { 'Authorization': `Bearer ${token}` };
    }
    
    return {};
  }

  /**
   * Fetch full user profile from backend
   */
  async fetchUserProfile(token: string): Promise<Partial<User>> {
    try {
      const sanitizedToken = token && token !== 'undefined' && token !== 'null' ? token : null;
      const response = await fetch('/api/user', {
        method: 'GET',
        headers: sanitizedToken
          ? {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${sanitizedToken}`
            }
          : {
              'Content-Type': 'application/json'
            },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user profile');
      }

      const userData = await response.json();
      return {
        bio: userData.bio || undefined,
        displayName: userData.displayName || undefined,
        firstName: userData.firstName || undefined,
        lastName: userData.lastName || undefined,
        phone: userData.phone || undefined,
        avatar: userData.avatar || undefined,
        kudozBalance: userData.kudozBalance || 0,
        lifetimeKudozEarned: userData.lifetimeKudozEarned || 0,
        accountBalance: userData.accountBalance || '0',
        trustScore: userData.trustScore ?? undefined,
        trustTier: userData.trustTier ?? undefined,
        totalReviews: userData.totalReviews ?? 0,
        totalOrders: userData.totalOrders ?? 0,
        profileCompletionPercent: userData.profileCompletionPercent ?? undefined
      };
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }

  /**
   * Make authenticated API call to Kudoz backend
   */
  async apiCall(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const headers = {
      'Content-Type': 'application/json',
      ...this.getAuthHeaders(),
      ...options.headers
    };

    return fetch(`/api${endpoint}`, {
      ...options,
      headers,
    });
  }

  /**
   * Silently refresh the Eatzy access token using the stored refresh token.
   * Returns true on success, false if the refresh token is unavailable or the refresh fails.
   * Guards against concurrent refresh calls with an instance flag.
   */
  async refreshEatzyToken(): Promise<boolean> {
    if (this._eatzyRefreshing) {
      // Another refresh is already in flight — wait briefly and return success optimistically
      await new Promise(r => setTimeout(r, 1000));
      return !!localStorage.getItem('eatzyUserToken');
    }
    const refreshToken = localStorage.getItem(this.EATZY_REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      console.warn('[AuthService] No Eatzy refresh token stored — cannot silent-refresh');
      return false;
    }
    this._eatzyRefreshing = true;
    try {
      const response = await fetch('/api/auth/refresh-eatzy-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        console.warn('[AuthService] Eatzy token refresh endpoint returned:', response.status);
        // 403 or 404 from the refresh endpoint means the refresh token itself is expired or
        // invalid. There is no silent path forward — force sign-out and redirect to login.
        // Also trigger if the response body contains eatzyTokenExpired regardless of status.
        if (response.status === 403 || response.status === 404) {
          const body = await response.json().catch(() => ({}));
          if (body.eatzyTokenExpired || response.status === 404) {
            console.warn('[AuthService] Eatzy refresh token expired — signing out and redirecting to login');
            this.signOut().catch(() => {});
            window.location.href = '/login?reason=session_expired';
            return false;
          }
        }
        return false;
      }
      const data = await response.json();
      if (!data.eatzyAccessToken) return false;
      localStorage.setItem('eatzyUserToken', data.eatzyAccessToken);
      sessionStorage.setItem('adminEatzyToken', data.eatzyAccessToken);
      sessionStorage.setItem('partnerEatzyToken', data.eatzyAccessToken);
      // Note: Eatzy's /account/refresh does not issue a new refresh token.
      // We keep the existing one and only update the access token + its expiry.
      if (data.eatzyTokenExpires) localStorage.setItem(this.EATZY_TOKEN_EXPIRES_KEY, String(data.eatzyTokenExpires));
      console.log('[AuthService] Eatzy access token silently refreshed');
      return true;
    } catch (error) {
      console.error('[AuthService] Error during Eatzy token refresh:', error);
      return false;
    } finally {
      this._eatzyRefreshing = false;
    }
  }

  async eatzyApiCall(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = localStorage.getItem(this.TOKEN_KEY);
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Proactive refresh: if the stored Eatzy token expires within 60 s, refresh it first
    const expiresStr = localStorage.getItem(this.EATZY_TOKEN_EXPIRES_KEY);
    if (expiresStr) {
      const expiresAt = Number(expiresStr);
      const nowSec = Math.floor(Date.now() / 1000);
      if (expiresAt > 0 && expiresAt - nowSec < 60) {
        console.log('[AuthService] Eatzy token expiring soon, proactively refreshing...');
        await this.refreshEatzyToken();
      }
    }

    const makeCall = async (): Promise<Response> => {
      const eatzyToken = localStorage.getItem('eatzyUserToken');
      const headers = {
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...(eatzyToken && { 'X-User-Eatzy-Token': eatzyToken }),
        ...options.headers,
      };
      const proxyEndpoint = `/api/eatzy${endpoint}`;
      const { cache, ...restOptions } = options;
      console.log('🔑 Using backend proxy for Eatzy API call:', proxyEndpoint);
      return fetch(proxyEndpoint, {
        cache: cache ?? 'no-store',
        ...restOptions,
        headers,
      });
    };

    const response = await makeCall();

    // Reactive refresh: if the proxy signals eatzyTokenExpired, try once then retry.
    // Covers both 401 and 403 since Eatzy uses 403 for expired tokens but some
    // proxy paths may surface a 401 instead.
    if (response.status === 401 || response.status === 403) {
      try {
        const cloned = response.clone();
        const errorData = await cloned.json().catch(() => ({}));
        if (errorData.eatzyTokenExpired) {
          console.log('[AuthService] Eatzy token expired (proxy signal), attempting silent refresh...');
          const refreshed = await this.refreshEatzyToken();
          if (refreshed) {
            console.log('[AuthService] Eatzy token refreshed, retrying request...');
            return makeCall();
          }
          // refreshEatzyToken returned false — no refresh token stored (consumer accounts
          // never receive one from /account/authorize). Clear the stale Eatzy token so
          // subsequent calls go directly to the Kudoz JWT path on the server, avoiding a
          // wasted round-trip with a token that the server already knows is expired.
          const refreshToken = localStorage.getItem(this.EATZY_REFRESH_TOKEN_KEY);
          if (!refreshToken) {
            console.warn('[AuthService] No refresh token — clearing stale Eatzy user token to skip future 403 retries');
            localStorage.removeItem('eatzyUserToken');
            localStorage.removeItem(this.EATZY_TOKEN_EXPIRES_KEY);
          }
          // If the refresh token was expired/invalid, refreshEatzyToken() will have already
          // triggered signOut + redirect. Just return the 403 to let callers surface an
          // EatzyTokenExpiredError UI.
        }
      } catch {
        // If response parsing fails, return the original response as-is
      }
    }

    return response;
  }

  /**
   * Store auth data
   */
  private setAuth(token: string, user: User): void {
    // Only store if both token and user are defined
    if (token && user) {
      localStorage.setItem(this.TOKEN_KEY, token);
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    } else {
      console.warn('⚠️ setAuth called with invalid data - token:', !!token, 'user:', !!user);
    }
  }

  /**
   * Update stored user data (used to sync enriched user data after verification)
   */
  updateStoredUser(user: User): void {
    if (user && user.id && user.email) {
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));
      console.log('💾 Updated stored user data with enriched fields');
    }
  }

  /**
   * Get cached user profile with TTL check
   */
  getCachedUserProfile(): any | null {
    try {
      const cached = localStorage.getItem(this.USER_CACHE_KEY);
      if (!cached) return null;
      
      const { data, timestamp } = JSON.parse(cached);
      const isExpired = (Date.now() - timestamp) > this.CACHE_TTL;
      
      if (isExpired) {
        localStorage.removeItem(this.USER_CACHE_KEY);
        return null;
      }
      
      console.log('📦 Using cached user profile');
      return data;
    } catch (error) {
      console.error('Error reading cached user profile:', error);
      localStorage.removeItem(this.USER_CACHE_KEY);
      return null;
    }
  }

  /**
   * Cache user profile with timestamp
   */
  setCachedUserProfile(userData: any): void {
    try {
      const cacheData = {
        data: userData,
        timestamp: Date.now()
      };
      localStorage.setItem(this.USER_CACHE_KEY, JSON.stringify(cacheData));
      console.log('💾 Cached user profile');
    } catch (error) {
      console.error('Error caching user profile:', error);
    }
  }

  /**
   * Get cached Eatzy profile
   */
  getCachedEatzyProfile(): any | null {
    try {
      const cached = localStorage.getItem(this.EATZY_CACHE_KEY);
      if (!cached) return null;
      
      const { data, timestamp } = JSON.parse(cached);
      const isExpired = (Date.now() - timestamp) > this.CACHE_TTL;
      
      if (isExpired) {
        localStorage.removeItem(this.EATZY_CACHE_KEY);
        return null;
      }
      
      console.log('📦 Using cached Eatzy profile');
      return data;
    } catch (error) {
      console.error('Error reading cached Eatzy profile:', error);
      localStorage.removeItem(this.EATZY_CACHE_KEY);
      return null;
    }
  }

  /**
   * Cache Eatzy profile
   */
  setCachedEatzyProfile(eatzyData: any): void {
    try {
      const cacheData = {
        data: eatzyData,
        timestamp: Date.now()
      };
      localStorage.setItem(this.EATZY_CACHE_KEY, JSON.stringify(cacheData));
      console.log('💾 Cached Eatzy profile');
    } catch (error) {
      console.error('Error caching Eatzy profile:', error);
    }
  }

  /**
   * Get user profile with smart caching
   */
  async getUserProfile(forceRefresh: boolean = false): Promise<any> {
    // Try cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = this.getCachedUserProfile();
      if (cached) {
        return cached;
      }
    }

    console.log('🔄 Fetching fresh user profile...');
    
    try {
      // Fetch from server
      const response = await this.apiCall('/user');
      
      if (!response.ok) {
        throw new Error(`Failed to fetch user profile: ${response.status}`);
      }

      const userData = await response.json();
      
      // Cache the result
      this.setCachedUserProfile(userData);
      
      return userData;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      
      // Fallback to stale cache if available
      const staleCache = localStorage.getItem(this.USER_CACHE_KEY);
      if (staleCache) {
        console.log('📦 Using stale cache as fallback');
        const { data } = JSON.parse(staleCache);
        return data;
      }
      
      throw error;
    }
  }

  /**
   * Get Eatzy profile with smart caching
   */
  async getEatzyProfile(forceRefresh: boolean = false): Promise<any> {
    // Try cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = this.getCachedEatzyProfile();
      if (cached) {
        return cached;
      }
    }

    console.log('🔄 Fetching fresh Eatzy profile...');
    
    try {
      const response = await this.eatzyApiCall('/account/profile');
      
      if (!response.ok) {
        throw new Error(`Failed to fetch Eatzy profile: ${response.status}`);
      }

      const eatzyData = await response.json();
        
        // Cache the result
        this.setCachedEatzyProfile(eatzyData);
        
        return eatzyData;
    } catch (error) {
      console.error('Error fetching Eatzy profile:', error);
      
      // Fallback to stale cache if available
      const staleCache = localStorage.getItem(this.EATZY_CACHE_KEY);
      if (staleCache) {
        console.log('📦 Using stale Eatzy cache as fallback');
        const { data } = JSON.parse(staleCache);
        return data;
      }
      
      throw error;
    }
  }

  /**
   * Clear all caches
   */
  clearAllCaches(): void {
    localStorage.removeItem(this.USER_CACHE_KEY);
    localStorage.removeItem(this.EATZY_CACHE_KEY);
    localStorage.removeItem('user_addresses_cache');
    localStorage.removeItem('eatzyUserToken');
    localStorage.removeItem(this.EATZY_REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.EATZY_TOKEN_EXPIRES_KEY);
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('user_addresses_cache_')) {
        localStorage.removeItem(key);
      }
    });
    console.log('🗑️ Cleared all caches');
  }

  /**
   * Clear auth data
   */
  private clearAuth(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.clearAllCaches(); // Also clear caches on logout
  }

  /**
   * Clear all auth data (for debugging)
   */
  clearAllAuthData(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.clearAllCaches();
    console.log('🗑️ Cleared all auth data');
  }
}

export const authService = new AuthService();
export type { User, AuthState, AuthResponse };
